import { useEffect, useState } from 'react';
import type { AndarBaharLegal, AndarBaharSide, AndarBaharView } from '@cardhub/engine';
import { PlayingCard } from '../cards/PlayingCard';
import { Coins } from '../components/ui';
import { useT } from '../i18n';
import { useGame } from '../store/game';
import { MySeat, useNarrow } from './common';

export function AndarBaharTable() {
  const t = useT();
  const view = useGame((s) => s.view) as AndarBaharView;
  const legal = useGame((s) => s.legal) as AndarBaharLegal | null;
  const seats = useGame((s) => s.seats);
  const mySeat = useGame((s) => s.mySeat);
  const turn = useGame((s) => s.turn);
  const act = useGame((s) => s.act);
  const acting = useGame((s) => s.acting);
  const narrow = useNarrow();
  const name = (s: number) => seats[s]?.user?.username ?? `Seat ${s + 1}`;
  const myTurn = mySeat !== null && turn?.seats.includes(mySeat);
  const me = mySeat !== null ? view.players[mySeat] : null;
  const betting = Boolean(myTurn && legal?.kind === 'bet');
  const [side, setSide] = useState<AndarBaharSide>('ANDAR');
  const [amount, setAmount] = useState<number>(view.config.minBet);
  const myResult = view.lastResult?.seats.find((r) => r.seat === mySeat);

  useEffect(() => {
    if (legal?.kind === 'bet') setAmount((a) => Math.max(legal.min, Math.min(a, legal.max)));
  }, [legal, view.handNo]);

  const quick = legal?.kind === 'bet' ? [...new Set([1, 2, 5, 10, 25].map((k) => legal.min * k).filter((v) => v <= legal.max).concat(legal.max))].sort((a, b) => a - b) : [];
  const bets = (s: AndarBaharSide) => view.players.filter((p) => p && p.status === 'BET' && p.side === s) as NonNullable<AndarBaharView['players'][number]>[];
  const pays = (s: AndarBaharSide) => (s === 'ANDAR' ? view.config.andarPayout : view.config.baharPayout) / 100;
  const dealing = view.phase === 'DEALING';
  const ended = view.phase === 'HAND_END';

  const row = (s: AndarBaharSide, cards: string[]) => {
    const won = view.winner === s;
    const list = bets(s);
    return (
      <div
        key={s}
        className="card glass col"
        style={{ width: '100%', padding: 8, gap: 6, borderColor: won ? 'var(--accent)' : undefined, boxShadow: won ? '0 0 0 2px var(--accent)' : undefined }}
        aria-label={s === 'ANDAR' ? t('ab.andar') : t('ab.bahar')}
      >
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <strong>{s === 'ANDAR' ? t('ab.andar') : t('ab.bahar')}</strong>
          <span className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{t('ab.pays', { n: pays(s) })}</span>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', minHeight: narrow ? 52 : 68, rowGap: 4 }}>
                    {cards.map((c, i) => (
            <div key={c} style={{ marginLeft: i === 0 ? 0 : -18, outline: won && view.joker && c[0] === view.joker[0] && i === cards.length - 1 ? '2px solid var(--accent)' : undefined, borderRadius: 8 }}>
              <PlayingCard id={c} size="sm" />
            </div>
          ))}
        </div>
        <div className="row" style={{ gap: 4, flexWrap: 'wrap' }}>
          {list.length === 0 ? (
            <span className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{t('ab.noBets')}</span>
          ) : (
            list.map((p) => (
              <span key={p.seat} className={`pill ${ended && won ? 'accent' : ''}`} style={{ fontSize: 'var(--fs-xs)' }}>
                {p.seat === mySeat ? t('cg.you') : name(p.seat)} · {p.bet}
                {ended && p.payout > 0 ? ` → ${p.payout}` : ''}
              </span>
            ))
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="stage">
        <div className="felt" />
        <div className="casino-layer spread">
          <div className="row" style={{ alignItems: 'center', gap: 12 }}>
            <div className="col" style={{ alignItems: 'center', gap: 2 }} aria-label={t('ab.joker')}>
              {view.joker ? <PlayingCard id={view.joker} size={narrow ? 'sm' : 'md'} /> : <PlayingCard faceDown size={narrow ? 'sm' : 'md'} />}
              <span className="center-note">{t('ab.joker')}</span>
            </div>
            <div className="col" style={{ gap: 2 }}>
              {ended && view.winner && (
                <span className="pill accent" role="status">
                  {t('ab.wins', { side: view.winner === 'ANDAR' ? t('ab.andar') : t('ab.bahar') })}
                </span>
              )}
              {ended && myResult && (
                <span className={`pill ${myResult.delta > 0 ? 'accent' : 'danger'}`} role="status">
                  {myResult.delta > 0 ? t('ab.youWon', { n: myResult.delta }) : t('ab.youLost', { n: -myResult.delta })}
                </span>
              )}
              {dealing && <span className="center-note">{t('ab.dealing')}</span>}
              {view.phase === 'WAITING' && <span className="center-note">{t('bj.waiting')}</span>}
              {view.phase === 'BETTING' && !myTurn && turn?.seats[0] !== undefined && <span className="center-note">{t('table.waitingFor', { name: name(turn.seats[0]) })}</span>}
            </div>
          </div>
          {row('ANDAR', view.andar)}
          {row('BAHAR', view.bahar)}
        </div>
        <MySeat seat={mySeat !== null ? seats[mySeat] : undefined} sub={me ? <Coins amount={me.chips} size={14} /> : null} />
      </div>
      <div className="table-bottom">
        {betting && legal?.kind === 'bet' && (
          <div className="bid-picker" role="group" aria-label={t('bj.placeBet')}>
            <div className="bid-head">
              <strong>{t('bj.placeBet')}</strong>
              <span className="dim">{t('bj.limits', { min: legal.min, max: legal.max })}</span>
            </div>
            <div className="row" style={{ gap: 8 }} role="radiogroup" aria-label={t('ab.side')}>
              {(['ANDAR', 'BAHAR'] as const).map((s) => (
                <button key={s} role="radio" aria-checked={side === s} className={`btn grow ${side === s ? 'primary' : ''}`} onClick={() => setSide(s)}>
                  {s === 'ANDAR' ? t('ab.andar') : t('ab.bahar')}
                </button>
              ))}
            </div>
            <div className="bid-chips amounts" role="radiogroup" aria-label={t('bj.placeBet')}>
              {quick.map((v) => (
                <button key={v} role="radio" aria-checked={amount === v} className={`bid-chip ${amount === v ? 'on' : ''}`} onClick={() => setAmount(v)}>
                  {v}
                </button>
              ))}
            </div>
            <div className="row" style={{ gap: 8 }}>
              {legal.canSitOut && (
                <button className="btn grow" disabled={acting} onClick={() => void act({ type: 'sitOut' })}>
                  {t('bj.sitOut')}
                </button>
              )}
              <button className="btn primary grow bid-confirm" disabled={acting} onClick={() => void act({ type: 'bet', side, amount })}>
                {t('ab.bet', { n: amount, side: side === 'ANDAR' ? t('ab.andar') : t('ab.bahar') })}
              </button>
            </div>
          </div>
        )}
        {!betting && (
          <div className="action-bar">
            <span style={{ fontSize: 'var(--fs-xs)', color: 'rgba(255,255,255,0.8)' }}>
              {me && me.status === 'BET' && me.side ? t('ab.yourBet', { side: me.side === 'ANDAR' ? t('ab.andar') : t('ab.bahar'), n: me.bet }) : ended ? t('bj.handOver') : dealing ? t('ab.dealing') : view.phase === 'BETTING' ? t('bj.betting') : t('bj.waiting')}
            </span>
          </div>
        )}
      </div>
    </>
  );
}
